import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Loader2, ChevronDown, ChevronRight, Home, Building2, ArrowRightLeft } from "lucide-react";
import { fetchProjects, fetchProjectPhases, fetchPropertyData, fetchPropertyConfigurations } from "../../services/projects";
import { transferProjectOwner, getDeveloperCustomers } from "../../services/adminProjects";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";

const asList = (r) => (Array.isArray(r) ? r : r?.items || []);

const PROP_STATUS = {
  Sold: "bg-emerald-50 text-emerald-700",
  Unsold: "bg-amber-50 text-amber-700",
};

const PROP_COLUMNS = [
  {
    key: "unit",
    header: "Unit",
    width: 160,
    render: (row) => {
      const p = row.propertyData || row;
      return (
        <span className="inline-flex items-center gap-1 text-slate-800">
          <Home size={12} className="text-slate-400" /> {p.buildingNo}-{p.propertyNo}
        </span>
      );
    },
  },
  { key: "config", header: "Config", width: 160, render: (row) => (row.propertyConfigData || {}).configName || null },
  { key: "type", header: "Type", width: 140, render: (row) => (row.propertyData || row).propertyType || null },
  {
    key: "status",
    header: "Status",
    width: 120,
    render: (row) => {
      const p = row.propertyData || row;
      return (
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${PROP_STATUS[p.status] || "bg-slate-100 text-slate-600"}`}>
          {p.status || "—"}
        </span>
      );
    },
  },
  {
    key: "handover",
    header: "Handover",
    width: 140,
    render: (row) => {
      const p = row.propertyData || row;
      return p.isHandover ? p.handoverDate || "Yes" : null;
    },
  },
];

/**
 * Read-only project detail for Zumale/BuildVizer admins: project header + phases, each expanding to
 * its properties + configurations. Reuses the existing project endpoints (unscoped for ZUMALE),
 * scoped to the project's own developer via the customerId passed from the list.
 */
const AdminProjectDetail = () => {
  const { projectId } = useParams();
  const [params] = useSearchParams();
  const customerId = params.get("customerId");
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({}); // phaseId -> { loading, properties, configs, expanded }

  // Transfer-ownership modal (internal project -> onboarded developer)
  const [transferOpen, setTransferOpen] = useState(false);
  const [developers, setDevelopers] = useState([]);
  const [targetId, setTargetId] = useState("");
  const [transferring, setTransferring] = useState(false);

  const openTransfer = async () => {
    setTransferOpen(true);
    try {
      const res = await getDeveloperCustomers();
      const items = res?.items || (Array.isArray(res) ? res : []);
      // /customers/subscription returns { customer, subscription } rows.
      setDevelopers(
        items
          .map((row) => row.customer || row)
          .filter((c) => c && c.customerId)
      );
    } catch (e) {
      toast.error("Could not load developer customers");
    }
  };

  const doTransfer = async () => {
    if (!targetId) {
      toast.error("Select the developer to transfer to");
      return;
    }
    setTransferring(true);
    try {
      await transferProjectOwner(projectId, Number(targetId));
      toast.success("Project ownership transferred");
      setTransferOpen(false);
      navigate("/zumale-admin/projects");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transfer failed");
    } finally {
      setTransferring(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [projs, ph] = await Promise.all([
        fetchProjects({ projectId, customerId }),
        fetchProjectPhases({ projectId }),
      ]);
      setProject(asList(projs)[0] || null);
      setPhases(asList(ph));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the project");
    } finally {
      setLoading(false);
    }
  }, [projectId, customerId]);

  useEffect(() => {
    load();
  }, [load]);

  const togglePhase = async (phase) => {
    const id = phase.projectPhaseId;
    const cur = open[id];
    if (cur?.expanded) {
      setOpen((o) => ({ ...o, [id]: { ...cur, expanded: false } }));
      return;
    }
    if (cur?.properties) {
      setOpen((o) => ({ ...o, [id]: { ...cur, expanded: true } }));
      return;
    }
    setOpen((o) => ({ ...o, [id]: { loading: true, expanded: true } }));
    try {
      const [props, configs] = await Promise.all([
        fetchPropertyData(projectId, customerId, id),
        fetchPropertyConfigurations({ projectId, customerId, projectPhaseId: id }),
      ]);
      setOpen((o) => ({ ...o, [id]: { loading: false, expanded: true, properties: asList(props), configs: asList(configs) } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load phase details");
      setOpen((o) => ({ ...o, [id]: { loading: false, expanded: true, properties: [], configs: [] } }));
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      </div>
    );
  }

  const subtitle = [
    project?.developer,
    project?.address?.city,
    `${phases.length} phase${phases.length === 1 ? "" : "s"}`,
    project?.reraNo ? `RERA ${project.reraNo}` : null,
  ]
    .filter(Boolean)
    .join("  ·  ");

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)]">
      <PageHeader
        icon={Building2}
        title={project?.projectName || "Project"}
        subtitle={subtitle}
        backTo="/zumale-admin/projects"
        backLabel="Back to projects"
        actions={
          <button
            onClick={openTransfer}
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/30 px-3 py-2 text-sm font-medium text-white hover:bg-white/10 transition-colors"
          >
            <ArrowRightLeft size={15} /> Transfer ownership
          </button>
        }
      />

      {transferOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-800">Transfer project ownership</h3>
            <p className="mt-1 text-sm text-slate-500">
              Move this internal project — with all its sales, leads, referrals, channel partners and
              interior executions — to an onboarded developer. Only projects currently owned by a
              BuildVizer-managed (internal) account can be transferred; the target must already have a
              subscription. Existing channel partners are retained; no new CPs can be added afterwards.
            </p>
            <label className="mt-4 block text-sm">
              <span className="text-slate-600">Transfer to developer</span>
              <select
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="">Select a developer…</option>
                {developers.map((c) => (
                  <option key={c.customerId} value={c.customerId}>
                    {c.companyName || c.ownerName || `Customer #${c.customerId}`} ({c.emailId})
                  </option>
                ))}
              </select>
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setTransferOpen(false)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={doTransfer}
                disabled={transferring}
                className="inline-flex items-center gap-1.5 rounded-lg bg-flamingo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-flamingo-700 disabled:opacity-50"
              >
                {transferring ? <Loader2 size={15} className="animate-spin" /> : <ArrowRightLeft size={15} />} Transfer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phases → properties + configs */}
      <div className="flex flex-col gap-3">
        {phases.map((ph) => {
          const st = open[ph.projectPhaseId] || {};
          return (
            <div key={ph.projectPhaseId} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <button onClick={() => togglePhase(ph)} className="flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left hover:bg-slate-50">
                <div>
                  <p className="text-sm font-semibold text-slate-800">{ph.name}</p>
                  <p className="text-xs text-slate-500">
                    {ph.towers ?? 0} tower{ph.towers === 1 ? "" : "s"} · {ph.units ?? 0} unit{ph.units === 1 ? "" : "s"}
                    {ph.isOccupancyDone ? " · Occupancy done" : ""}
                    {ph.reraNo ? ` · RERA ${ph.reraNo}` : ""}
                  </p>
                </div>
                {st.expanded ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
              </button>

              {st.expanded && (
                <div className="border-t border-slate-100 px-5 py-4">
                  {st.loading ? (
                    <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> Loading…</div>
                  ) : (
                    <div className="flex flex-col gap-4">
                      {/* Configurations */}
                      {st.configs?.length > 0 && (
                        <div>
                          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Configurations</p>
                          <div className="flex flex-wrap gap-1.5">
                            {st.configs.map((c) => (
                              <span key={c.propertyConfigId} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700">
                                {c.configName}{c.sizeBhk ? ` · ${c.sizeBhk} BHK` : ""}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Properties */}
                      <div>
                        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Properties ({st.properties?.length || 0})
                        </p>
                        <DataTable
                          columns={PROP_COLUMNS}
                          data={st.properties || []}
                          minWidth={620}
                          rowKey={(row) => (row.propertyData || row).propertyId}
                          emptyState={<div className="px-6 py-10 text-center text-sm text-slate-400">No properties in this phase.</div>}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {phases.length === 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-400">This project has no phases.</div>
        )}
      </div>
    </div>
  );
};

export default AdminProjectDetail;
